import React, { useEffect, useMemo, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import ClickSpark from "./ClickSpark";
import Header from "./Header";
import "./LandingPage.css";
import Lenis from "lenis";
import {
  FaCalendarAlt,
  FaBullhorn,
  FaMoneyCheckAlt,
  FaUserCheck,
  FaPlane,
  FaIdCard,
  FaUserPlus,
  FaArrowUp,
} from "react-icons/fa";

// ---------------------------------------------------------
//  Features Data
// ---------------------------------------------------------
const features = [
  {
    icon: <FaUserCheck />,
    title: "Attendance Tracking",
    desc: "Easily monitor employee check-ins, check-outs, and working hours.",
    tag: "Stay Organized",
  },
  {
    icon: <FaPlane />,
    title: "Leave Management",
    desc: "Simplified leave requests and approval workflows for all employees.",
    tag: "Boost Productivity",
  },
  {
    icon: <FaMoneyCheckAlt />,
    title: "Payroll Automation",
    desc: "Accurate salary calculations and payslip generation every month.",
    tag: "Save Time",
  },
  {
    icon: <FaCalendarAlt />,
    title: "Holiday Calendar",
    desc: "Maintain and display an organization-wide list of holidays.",
    tag: "Plan Ahead",
  },
  {
    icon: <FaBullhorn />,
    title: "Announcements",
    desc: "Instantly share company updates and HR announcements with everyone.",
    tag: "Stay Informed",
  },
  {
    icon: <FaIdCard />,
    title: "KYC Verification",
    desc: "Capture and verify employee KYC documents securely and quickly.",
    tag: "Ensure Compliance",
  },
  {
    icon: <FaUserPlus />,
    title: "Employee Onboarding",
    desc: "Streamlined onboarding workflows with tasks, docs, and checklists.",
    tag: "Smooth Transitions",
  },
  {
    icon: <FaUserPlus />,
    title: "Employee Tracking",
    desc: "Track login locations and IPs to help admins manage the organization efficiently.",
    tag: "Enhanced Oversight",
  },
  {
    title: "Seamless Integration",
    desc: "Our HRMS integrates seamlessly with your organization’s workflow, making management effortless.",
    tag: "Easy Connectivity",
  },
  {
    title: "Exceptional Security",
    desc: "We ensure your employee and payroll data stays safe with top-grade encryption and access control.",
    tag: "Data Protection",
  },
];

// ---------------------------------------------------------
// 🚀 Main Component
// ---------------------------------------------------------
export default function LandingPage() {
  const [billing, setBilling] = useState("monthly");
  const [advanceCount, setAdvanceCount] = useState(50);
  const [premiumCount, setPremiumCount] = useState(50);
  const [showScroll, setShowScroll] = useState(false);

  const prices = useMemo(
    () => ({
      monthly: { advance: 60, premium: 85 },
      yearly: { advance: 700, premium: 1000 },
    }),
    []
  );

  const heroRef = useRef();
  const featuresRef = useRef();
  const pricingRef = useRef();

  const [heroVisible, setHeroVisible] = useState(false);
  const [featuresVisible, setFeaturesVisible] = useState(false);
  const [pricingVisible, setPricingVisible] = useState(false);
  const [featureCardsVisible, setFeatureCardsVisible] = useState(
    Array(features.length).fill(false)
  );
  const [priceCardsVisible, setPriceCardsVisible] = useState([
    false,
    false,
    false,
  ]);

  // ✅ Initialize Lenis
 useEffect(() => {
  // Detect if the user is on mobile
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  if (isMobile) {
    // ✅ Allow normal scroll on mobile
    document.documentElement.style.overflowY = "auto";
    document.body.style.overflowY = "auto";
    document.body.style.touchAction = "pan-y";
    return; // ❌ Skip Lenis setup on mobile
  }

  // ✅ Enable Lenis on desktop
  const lenis = new Lenis({
    duration: 1.1,
    smoothWheel: true,
    smoothTouch: true,
    direction: "vertical",
    gestureDirection: "vertical",
    normalizeWheel: true,
    touchMultiplier: 2.0,
  });

  window.lenis = lenis;

  function raf(time) {
    lenis.raf(time);
    requestAnimationFrame(raf);
  }
  requestAnimationFrame(raf);

  lenis.on("scroll", ({ scroll }) => {
    setShowScroll(scroll > 300);
  });

  return () => {
    lenis.destroy();
  };
}, []);


  // ✅ Animate Hero / Features / Pricing
  useEffect(() => {
    const options = { threshold: 0.12 };
    const heroObs = new IntersectionObserver(
      ([entry]) => setHeroVisible(entry.isIntersecting),
      options
    );
    const featuresObs = new IntersectionObserver(
      ([entry]) => setFeaturesVisible(entry.isIntersecting),
      options
    );
    const pricingObs = new IntersectionObserver(
      ([entry]) => setPricingVisible(entry.isIntersecting),
      options
    );

    if (heroRef.current) heroObs.observe(heroRef.current);
    if (featuresRef.current) featuresObs.observe(featuresRef.current);
    if (pricingRef.current) pricingObs.observe(pricingRef.current);

    return () => {
      heroObs.disconnect();
      featuresObs.disconnect();
      pricingObs.disconnect();
    };
  }, []);

  // ✅ Animate feature cards
  useEffect(() => {
    if (featuresVisible) {
      features.forEach((_item, i) => {
        setTimeout(() => {
          setFeatureCardsVisible((prev) => {
            const next = [...prev];
            next[i] = true;
            return next;
          });
        }, 80 * i);
      });
    }
  }, [featuresVisible, features.length]);

  // ✅ Animate pricing cards
  useEffect(() => {
    if (pricingVisible) {
      [0, 1, 2].forEach((i) => {
        setTimeout(() => {
          setPriceCardsVisible((prev) => {
            const next = [...prev];
            next[i] = true;
            return next;
          });
        }, 120 * i);
      });
    }
  }, [pricingVisible]);

  const handleGetStarted = () => {
    pricingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const handleSignUp = () => {
    pricingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const advUnit = prices[billing].advance;
  const premUnit = prices[billing].premium;

  // ✅ Scroll-to-top function
  const scrollToTop = () => {
    if (window.lenis) {
      window.lenis.scrollTo(0, { immediate: false });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div data-lenis-root>
      <div className="lg-root">
        <div className="lg-bg" />
        <Header />

        <ClickSpark
          sparkColor="#fff"
          sparkSize={10}
          sparkRadius={15}
          sparkCount={8}
          duration={400}
        >
          {/* Hero Section */}
          <section
            ref={heroRef}
            className={`topheading slide-up${heroVisible ? " visible" : ""}`}
          >
            <div className="container topheading-inner">
              <div className="topheading-text">
                <div className="topheading-badge">
                  ✨ New: HR Integration Just Landed
                </div>
                <h1 className="topheading-title">
                  Simplify your <span className="highlight">HR Operations</span>{" "}
                  effortlessly
                </h1>
                <p className="topheading-desc">
                  Manage attendance, payroll, onboarding, and compliance — all
                  from one smart, secure platform designed to help your
                  organization grow faster.
                </p>
                <div className="topheading-buttons">
                  <button className="btn primary" onClick={handleGetStarted}>
                    Get Started
                  </button>
                  <button className="btn ghost" onClick={handleSignUp}>
                    View Demo
                  </button>
                </div>
              </div>
              <div className="topheading-image glass">
                <img
                  src={require("../assets/landing-image.png")}
                  alt="HRMS Dashboard"
                />
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section
            ref={featuresRef}
            className={`squarecube slide-up${featuresVisible ? " visible" : ""}`}
          >
            <div className="container">
              <h3 className="squarecube-title">What We Offer</h3>
              <div className="squarecube-grid desktop-view">
                {features.map((feature, i) => (
                  <div
                    key={i}
                    className={`squarecube-card slide-up${
                      featureCardsVisible[i] ? " visible" : ""
                    }`}
                  >
                    <div className="squarecube-bg"></div>
                    <div className="squarecube-content">
                      <h4 className="squarecube-heading">{feature.title}</h4>
                      <p className="squarecube-desc">{feature.desc}</p>
                      <div className="squarecube-footer">
                        <span className="squarecube-tag">{feature.tag}</span>
                        <button className="squarecube-arrow">➜</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Mobile Carousel */}
              <div className="squarecube-carousel mobile-view">
                <Swiper
                  modules={[Pagination]}
                  pagination={{ clickable: true }}
                  spaceBetween={20}
                  slidesPerView={1}
                  loop={true}
                >
                  {features.map((feature, i) => (
                    <SwiperSlide key={i}>
                      <div
                        className={`squarecube-card slide-up${
                          featuresVisible ? " visible" : ""
                        }`}
                      >
                        <div className="squarecube-bg"></div>
                        <div className="squarecube-content">
                          <h4 className="squarecube-heading">{feature.title}</h4>
                          <p className="squarecube-desc">{feature.desc}</p>
                          <div className="squarecube-footer">
                            <span className="squarecube-tag">{feature.tag}</span>
                            <button className="squarecube-arrow">➜</button>
                          </div>
                        </div>
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </div>
          </section>

          {/* Pricing Section */}
          <section
            ref={pricingRef}
            className={`pricesection slide-up${pricingVisible ? " visible" : ""}`}
          >
            <div className="container">
              <h2 className="price-heading">Commence your HR Journey</h2>
              <p className="price-subtext">
                Select from our flexible pricing plans to access powerful HR
                tools, seamless integrations, and dedicated support—tailored to
                fit your organization’s needs.
              </p>
              <div className="billing-toggle">
                <button
                  type="button"
                  className={billing === "monthly" ? "active" : ""}
                  onClick={() => setBilling("monthly")}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  className={billing === "yearly" ? "active" : ""}
                  onClick={() => setBilling("yearly")}
                >
                  Yearly
                </button>
                <div className={`indicator ${billing}`} />
              </div>

              {/* Pricing Cards */}
              <div className="price-grid">
                {/* Free */}
                <div
                  className={`price-card slide-up${
                    priceCardsVisible[0] ? " visible" : ""
                  }`}
                >
                  <h4>Free Plan</h4>
                  <p className="price">₹0</p>
                  <p className="price-period">Basic access</p>
                  <ul>
                    <li>2 users access only</li>
                    <li>Attendance only</li>
                    <li className="disabled">Leave management</li>
                    <li className="disabled">KYC verification</li>
                  </ul>
                  <button className="btn primary" onClick={handleSignUp}>
                    Start Trial
                  </button>
                </div>

                {/* Advance */}
                <div
                  className={`price-card featured slide-up${
                    priceCardsVisible[1] ? " visible" : ""
                  }`}
                >
                  <div className="lg-badge">Popular</div>
                  <h4>Advance</h4>
                  <p className="price">
                    ₹{advUnit}/employee/{billing === "monthly" ? "mo" : "yr"}
                  </p>
                  <div className="lg-slider-wrap">
                    <label htmlFor="advanceEmployees">
                      Employees:{" "}
                      <span className="lg-emp-count">{advanceCount}</span>
                    </label>
                    <input
                      id="advanceEmployees"
                      type="range"
                      min="1"
                      max="500"
                      value={advanceCount}
                      onChange={(e) => setAdvanceCount(Number(e.target.value))}
                      className="lg-range"
                    />
                    <div className="lg-range-scale">
                      <span>1</span>
                      <span>500</span>
                    </div>
                  </div>
                  <div className="lg-calc-total">
                    Estimated total:{" "}
                    <strong>
                      ₹{(advUnit * advanceCount).toLocaleString("en-IN")}
                    </strong>
                  </div>
                  <ul>
                    <li>Attendance</li>
                    <li>Leave management</li>
                    <li>KYC verification</li>
                    <li>Employee onboarding</li>
                  </ul>
                  <button className="btn accent" onClick={handleSignUp}>
                    Choose {billing === "monthly" ? "Monthly" : "Yearly"}
                  </button>
                </div>

                {/* Premium */}
                <div
                  className={`price-card slide-up${
                    priceCardsVisible[2] ? " visible" : ""
                  }`}
                >
                  <h4>Premium</h4>
                  <p className="price">
                    ₹{premUnit}/employee/{billing === "monthly" ? "mo" : "yr"}
                  </p>
                  <div className="lg-slider-wrap">
                    <label htmlFor="premiumEmployees">
                      Employees:{" "}
                      <span className="lg-emp-count">{premiumCount}</span>
                    </label>
                    <input
                      id="premiumEmployees"
                      type="range"
                      min="1"
                      max="500"
                      value={premiumCount}
                      onChange={(e) => setPremiumCount(Number(e.target.value))}
                      className="lg-range"
                    />
                    <div className="lg-range-scale">
                      <span>1</span>
                      <span>500</span>
                    </div>
                  </div>
                  <div className="lg-calc-total">
                    Estimated total:{" "}
                    <strong>
                      ₹{(premUnit * premiumCount).toLocaleString("en-IN")}
                    </strong>
                  </div>
                  <ul>
                    <li>Attendance + Leave + Payroll</li>
                    <li>Announcements + Holidays</li>
                    <li>KYC & Onboarding</li>
                    <li>All advanced HR features</li>
                  </ul>
                  <button className="btn primary" onClick={handleSignUp}>
                    Go {billing === "monthly" ? "Monthly" : "Yearly"}
                  </button>
                </div>
              </div>

              {/* Compare Plans */}
              <div className="compare-section">
                <h3>Compare Plans</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Feature</th>
                      <th>Free</th>
                      <th>Advance</th>
                      <th>Premium</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Attendance</td>
                      <td>✔</td>
                      <td>✔</td>
                      <td>✔</td>
                    </tr>
                    <tr>
                      <td>Leave Management</td>
                      <td>—</td>
                      <td>✔</td>
                      <td>✔</td>
                    </tr>
                    <tr>
                      <td>Payroll</td>
                      <td>—</td>
                      <td>—</td>
                      <td>✔</td>
                    </tr>
                    <tr>
                      <td>KYC & Onboarding</td>
                      <td>—</td>
                      <td>✔</td>
                      <td>✔</td>
                    </tr>
                    <tr>
                      <td>Announcements + Holidays</td>
                      <td>—</td>
                      <td>—</td>
                      <td>✔</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </ClickSpark>

        {/* Scroll-to-top */}
        {showScroll && (
          <button className="scroll-to-top" onClick={scrollToTop}>
            <FaArrowUp />
          </button>
        )}

        {/* Footer */}
        <footer className="footer">
          <div className="footer-inner">
            © {new Date().getFullYear()} UrbanGoTechLLP. All rights reserved.
          </div>
        </footer>
      </div>
    </div>
  );
}
